import React, { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { Box } from "@mui/material";
import CreateCampaignModal from "./CreateCampaignModal";
import { CloseIcon, CheckIcon, ChevronDownIcon } from "../../../components/commonUI/Icons/SvgIcon";
import "../../../js/styles/link-to-campaign-modal.css";

const LinkToCampaignModal = ({
  discount,
  campaigns,
  selectedCampaign,
  onSelectCampaign,
  onClose,
  campaignDropdownOpen,
  setCampaignDropdownOpen,
  campaignDropdownRef,
  campaignDropdownPosition,
  setCampaignDropdownPosition,
  campaignsLoading,
  userDetails,
  onCampaignsChange,
}) => {
  const dropdownPanelRef = useRef(null);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);

  useEffect(() => {
    const checkDropdownPosition = () => {
      if (campaignDropdownRef?.current && dropdownPanelRef?.current) {
        const rect = campaignDropdownRef.current.getBoundingClientRect();
        const panelHeight = dropdownPanelRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < panelHeight && spaceAbove > panelHeight) {
          setCampaignDropdownPosition("top");
        } else {
          setCampaignDropdownPosition("bottom");
        }
      }
    };

    if (campaignDropdownOpen) {
      setTimeout(checkDropdownPosition, 0);
    }
  }, [campaignDropdownOpen, setCampaignDropdownPosition]);

  const handleCampaignScroll = () => {
    // Handle infinite scroll or load more campaigns
  };

  const handleCreateCampaignSuccess = (newCampaign) => {
    // Add new campaign to the list
    if (onCampaignsChange) {
      onCampaignsChange([...campaigns, newCampaign]);
    }
    // Select the newly created campaign
    onSelectCampaign(newCampaign._id || newCampaign.id);
    setCampaignDropdownOpen(false);
    setShowCreateCampaignModal(false);
  };

  const handleConfirm = () => {
    // Handle campaign assignment
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Box
      sx={{ width: "100%" }}
      role="presentation"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="discount-modal-overlay"></div>
      <div className="right-modal discount-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>
              Link to Campaign
            </h2>
            <button
              className="modal-close-btn"
              onClick={handleCancel}
              type="button"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <div>
            <div className="form-group">
              <label>
                Assign to campaign
              </label>
              <div className="client-custom-dropdown" ref={campaignDropdownRef}>
                <button
                  type="button"
                  className={`client-dropdown-trigger ${
                    campaignDropdownOpen ? "open" : ""
                  }`}
                  onClick={() => setCampaignDropdownOpen((prev) => !prev)}
                >
                  <span className="client-trigger-value">
                    {selectedCampaign
                      ? campaigns.find(
                          (c) => (c._id || c.id) === selectedCampaign
                        )?.name || selectedCampaign
                      : "No campaign"}
                  </span>
                  <ChevronDownIcon className="client-trigger-chevron" />
                </button>
                {campaignDropdownOpen && (
                  <div
                    className={`client-dropdown-panel campaign-dropdown-panel ${
                      campaignDropdownPosition === "top"
                        ? "dropdown-top"
                        : "dropdown-bottom"
                    }`}
                    ref={dropdownPanelRef}
                  >
                    <div
                      className="client-dropdown-options"
                      onScroll={handleCampaignScroll}
                    >
                      <div
                        className="client-option-row campaign-option-row"
                        onClick={() => {
                          onSelectCampaign("");
                          setCampaignDropdownOpen(false);
                        }}
                      >
                        {!selectedCampaign && (
                          <CheckIcon className="campaign-check-icon" width={14} height={14} />
                        )}
                        <span className="client-option-name">No campaign</span>
                      </div>
                      {campaigns.length === 0 && !campaignsLoading && (
                        <div className="client-dropdown-empty">
                          No campaigns found.
                        </div>
                      )}
                      {campaigns.map((campaign) => (
                        <div
                          key={campaign._id || campaign.id}
                          className="client-option-row campaign-option-row"
                          onClick={() => {
                            onSelectCampaign(campaign._id || campaign.id);
                            setCampaignDropdownOpen(false);
                          }}
                        >
                          {selectedCampaign === (campaign._id || campaign.id) && (
                            <svg
                              className="campaign-check-icon"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                          <span className="client-option-name">
                            {campaign.name}
                          </span>
                        </div>
                      ))}
                      {campaigns.length > 0 && campaignsLoading && (
                        <div className="client-dropdown-loading">
                          Loading more...
                        </div>
                      )}
                    </div>
                    <div
                      className="client-option-row campaign-create-new"
                      onClick={() => {
                        setCampaignDropdownOpen(false);
                        setShowCreateCampaignModal(true);
                      }}
                    >
                      <span>+ Create new campaign</span>
                    </div>
                  </div>
                )}
              </div>
              <p className="coupon-description">
                Optional internal context for why this code was created.
              </p>
            </div>

            {discount?.campaigns && discount?.campaigns?.length > 0 && (
              <div className="campaign-details-section">
                <h3>Campaigns</h3>
                {discount?.campaigns.map((campaign) => (
                  <div
                    key={campaign?._id || campaign?.id}
                    className="campaign-details-card"
                  >
                    <div className="campaign-details-header">
                      <h4 className="campaign-details-title">
                        {campaign?.name}
                      </h4>
                      <span
                        className={`campaign-status-badge ${(
                          campaign?.status || ""
                        ).toLowerCase()}`}
                      >
                        {campaign?.status}
                      </span>
                    </div>
                    {campaign?.description && (
                      <div className="campaign-details-item">
                        <label>Description</label>
                        <p>{campaign?.description}</p>
                      </div>
                    )}

                    {campaign?.createdAt && (
                      <div className="campaign-details-item">
                        <label>Created At</label>
                        <p>
                          {moment(campaign?.createdAt)
                            ?.tz("Europe/London")
                            .format("DD MMM YYYY")}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            type="button"
          >
            Confirm
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>

      <CreateCampaignModal
        show={showCreateCampaignModal}
        onClose={() => setShowCreateCampaignModal(false)}
        onSuccess={handleCreateCampaignSuccess}
        userDetails={userDetails}
      />
    </Box>
  );
};

export default LinkToCampaignModal;
